import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { invoiceTargetOptions } from "./feeUtils";

const NONE = "none";

// Shared "who paid / invoiced to / how paid" fields for the add + edit fee forms.
export function FeeDetailFields({ value, onChange, project = {} }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const targets = invoiceTargetOptions(project);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Paid by</label>
          <Select value={value.paid_by || NONE} onValueChange={(v) => set({ paid_by: v === NONE ? "" : v, ...(v === "twp" ? {} : { reimbursable: false, reimbursed: false, reimbursed_date: "" }) })}>
            <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="fee-paid-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Not set</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="twp">TWP</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Invoiced to</label>
          <Select value={value.invoiced_to_type || NONE} onValueChange={(v) => set({ invoiced_to_type: v === NONE ? "" : v })}>
            <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="fee-invoiced-to"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Not set</SelectItem>
              {targets.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {value.invoiced_to_type === "custom" && (
        <Input value={value.invoiced_to_name || ""} onChange={(e) => set({ invoiced_to_name: e.target.value })}
          placeholder="Who should this be invoiced to?" className="rounded-sm" data-testid="fee-invoiced-to-name" />
      )}

      {value.paid_by === "twp" && (
        <div className="rounded-sm border border-violet-200 bg-violet-50/60 p-3 space-y-2" data-testid="fee-reimbursement">
          <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer font-medium">
            <input type="checkbox" checked={!!value.reimbursable}
              onChange={(e) => set({ reimbursable: e.target.checked, ...(e.target.checked ? {} : { reimbursed: false, reimbursed_date: "" }) })}
              className="h-4 w-4 rounded-sm accent-violet-700" data-testid="fee-reimbursable" />
            Recoverable from client (TWP paid on their behalf)
          </label>
          {value.reimbursable && (
            <div className="pl-6 space-y-2">
              <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
                <input type="checkbox" checked={!!value.reimbursed}
                  onChange={(e) => set({ reimbursed: e.target.checked, ...(e.target.checked ? {} : { reimbursed_date: "" }) })}
                  className="h-4 w-4 rounded-sm accent-emerald-700" data-testid="fee-reimbursed" />
                Reimbursed by client
              </label>
              {value.reimbursed && (
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Reimbursed on</label>
                  <Input type="date" value={value.reimbursed_date || ""} onChange={(e) => set({ reimbursed_date: e.target.value })}
                    className="rounded-sm mt-1 max-w-[200px]" data-testid="fee-reimbursed-date" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Payment method</label>
          <Input value={value.payment_method || ""} onChange={(e) => set({ payment_method: e.target.value })}
            placeholder="e.g. Bank transfer, Card" className="rounded-sm mt-1" data-testid="fee-payment-method" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Payment reference</label>
          <Input value={value.payment_reference || ""} onChange={(e) => set({ payment_reference: e.target.value })}
            placeholder="e.g. receipt / transaction #" className="rounded-sm mt-1" data-testid="fee-payment-reference" />
        </div>
      </div>
    </div>
  );
}
